import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress,
  InputAdornment
} from '@mui/material';
import {
  Phone as PhoneIcon,
  Business as BusinessIcon,
  Message as MessageIcon
} from '@mui/icons-material';
import api from '../services/api';

interface DomainRequestDialogProps {
  open: boolean;
  onClose: () => void;
  userData: {
    firstName: string;
    lastName: string;
    email: string;
  };
  domain: string;
}

const DomainRequestDialog: React.FC<DomainRequestDialogProps> = ({
  open,
  onClose,
  userData,
  domain
}) => {
  const [formData, setFormData] = useState({
    phone: '',
    companyName: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      console.log('🔍 DomainRequestDialog: Iniciando envío...');
      
      // Intentar crear una nueva solicitud
      try {
        console.log('🔍 DomainRequestDialog: Intentando crear nueva solicitud...');
        await api.createDomainRequest({
          firstName: userData.firstName,
          lastName: userData.lastName,
          email: userData.email,
          phone: formData.phone,
          companyName: formData.companyName,
          message: formData.message,
          source: 'registration'
        });
        console.log('✅ DomainRequestDialog: Solicitud creada exitosamente');
      } catch (createError: any) {
        console.log('🔍 DomainRequestDialog: Error al crear:', createError.response?.status, createError.response?.data?.message);
        
        // Si falla porque ya existe, intentar actualizar usando el email como identificador
        if (createError.response?.status === 400 && createError.response?.data?.message?.includes('Ya existe una solicitud')) {
          console.log('🔍 DomainRequestDialog: Solicitud ya existe, intentando actualizar por email...');
          
          // Intentar actualizar usando un endpoint especial que busque por email
          try {
            console.log('🔍 DomainRequestDialog: Actualizando por email:', userData.email);
            console.log('🔍 DomainRequestDialog: Datos a actualizar:', {
              phone: formData.phone,
              companyName: formData.companyName,
              message: formData.message
            });
            
            // Usar el método público del servicio API
            const updateData = await api.updateDomainRequestByEmail(userData.email, {
              phone: formData.phone,
              companyName: formData.companyName,
              message: formData.message
            });
            console.log('✅ DomainRequestDialog: Actualización exitosa:', updateData);
          } catch (updateError: any) {
            console.error('❌ DomainRequestDialog: Error en actualización:', updateError);
            console.error('❌ DomainRequestDialog: Error status:', updateError.response?.status);
            console.error('❌ DomainRequestDialog: Error data:', updateError.response?.data);
            console.error('❌ DomainRequestDialog: Error config URL:', updateError.config?.url);
            throw new Error(`No se pudo actualizar la solicitud existente: ${updateError.response?.data?.message || updateError.message}`);
          }
        } else {
          throw createError;
        }
      }

      setSuccess(true);
      setTimeout(() => {
        onClose();
        setSuccess(false);
        setFormData({ phone: '', companyName: '', message: '' });
      }, 2000);

    } catch (err: any) {
      console.error('❌ DomainRequestDialog: Error final:', err);
      console.error('❌ DomainRequestDialog: Error response:', err.response);
      setError(err.response?.data?.message || err.message || 'Error enviando solicitud');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
      setFormData({ phone: '', companyName: '', message: '' });
      setError('');
      setSuccess(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={loading}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={2}>
          <BusinessIcon color="primary" />
          <Typography variant="h6">
            Tu empresa no está registrada
          </Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {success ? (
          <Box textAlign="center" py={2}>
            <Alert severity="success" sx={{ mb: 2 }}>
              ¡Solicitud enviada exitosamente!
            </Alert>
            <Typography variant="body2" color="text.secondary">
              Nos pondremos en contacto contigo pronto para ayudarte a configurar el acceso de tu empresa.
            </Typography>
          </Box>
        ) : (
          <Box>
            <Typography variant="body1" sx={{ mb: 3 }}>
              El dominio <strong>{domain}</strong> no está registrado en nuestro sistema. 
              ¿Te gustaría que nos pongamos en contacto contigo para configurar el acceso de tu empresa?
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <form onSubmit={handleSubmit}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  label="Número de teléfono"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="+1 (555) 123-4567"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PhoneIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                  helperText="Para que podamos contactarte directamente"
                />

                <TextField
                  label="Nombre de la empresa (opcional)"
                  name="companyName"
                  value={formData.companyName}
                  onChange={handleChange}
                  placeholder="Ej: Mi Empresa S.A."
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <BusinessIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                />

                <TextField
                  label="Mensaje adicional (opcional)"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  multiline
                  rows={3}
                  placeholder="Cuéntanos más sobre tu empresa y necesidades de formación..."
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1 }}>
                        <MessageIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
            </form>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        {!success && (
          <>
            <Button 
              onClick={handleClose} 
              disabled={loading}
              color="inherit"
            >
              Cancelar
            </Button>
            <Button 
              onClick={handleSubmit}
              variant="contained"
              disabled={loading || !formData.phone.trim()}
              startIcon={loading ? <CircularProgress size={20} /> : null}
            >
              {loading ? 'Enviando...' : 'Enviar Solicitud'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default DomainRequestDialog;
